import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import styled from 'styled-components';
import { colorTextAdditional } from '../../../utils/palette';

const AXIS_COLOR = '#646777';
const GRID_COLOR = '#EEF0F3';

const Wrapper = styled.div`
  width: 100%;
  height: 180px;
`;

const UnitLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  //color: ${colorTextAdditional};
  margin-bottom: 8px;
`;

function formatDate(name) {
  try {
    return new Date(name).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return name;
  }
}

export default function TimeSeriesChart({ data, formatFn, unitLabel, colour = '#3E6CA8' }) {
  const { data: points = [] } = data ?? {};

  // Compute Y-axis width from the widest tick label so values are never clipped
  const yAxisWidth = useMemo(() => {
    if (!points.length) return 45;
    const maxVal = Math.max(...points.map((p) => Math.abs(p.value ?? 0)));
    const sample = formatFn ? formatFn(maxVal) : String(Math.round(maxVal));
    return Math.max(45, sample.length * 4 + 8);
  }, [points, formatFn]);

  if (!points.length) return null;

  const gradientId = `areaGradient-${colour.replace('#', '')}`;

  return (
    <>
      {unitLabel && <UnitLabel>{unitLabel}</UnitLabel>}
      <Wrapper>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colour} stopOpacity={0.12} />
                <stop offset="100%" stopColor={colour} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
            <XAxis
              dataKey="name"
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: AXIS_COLOR }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              width={yAxisWidth}
              tick={{ fontSize: 11, fill: AXIS_COLOR }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 6 }}
              labelFormatter={formatDate}
              formatter={(value) => [formatFn ? formatFn(value) : value, '']}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={colour}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              fillOpacity={1}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Wrapper>
    </>
  );
}
